import * as THREE from 'three';

/**
 * PBR material discipline for realistic glTF assets (Poly Haven / AmbientCG,
 * all CC0). This is the single source of truth for the material rules the other
 * consumers must follow once the realistic hero/prop assets land:
 *
 *  1. Metallic-roughness PBR -> `MeshStandardMaterial` / `MeshPhysicalMaterial`.
 *  2. Colour space: base-colour and emissive maps are sRGB; normal, roughness,
 *     metalness, AO, bump and alpha maps stay linear (`NoColorSpace`).
 *  3. ORM packing: AO -> R, Roughness -> G, Metalness -> B in one RGB texture.
 *     three r184 samples `.r` for the AO map, `.g` for the roughness map and
 *     `.b` for the metalness map, so one shared texture drives all three and
 *     saves two texture slots per material.
 *  4. One material per prop/category; identical props go through
 *     `ModelLibrary.instance()` / `InstancedMesh`, never per-instance clones.
 *  5. Textures stay at 1K, WebP albedo only, normals/ORM as PNG (the offline
 *     `scripts/optimize-models.mjs` enforces this; the runtime just clamps
 *     anisotropy).
 *  6. Baked sails/rigging on the hero ships are hidden — the procedural cloth
 *     sails and server-driven yards/masts own that role.
 *
 * `GLTFLoader` already assigns the correct colour spaces and wires the glTF
 * metallic-roughness maps, so most of this module is a safe re-assertion plus
 * the ORM/atlas/anisotropy hooks for the authored LOD pipeline.
 */

/** Authored, shareable rule constants (also exported for tooling/checks). */
export const PBR_MATERIAL_RULES = {
  /** ORM channel convention (three r184 hardcodes these channel reads). */
  ormChannels: { ao: 'r', roughness: 'g', metalness: 'b' } as const,
  /** Clamp every texture's anisotropy to this value. */
  maxAnisotropy: 8,
  /** Maximum runtime texture dimension for the realistic tier (1K). */
  maxTextureSize: 1024,
  /** Baked materials to hide (procedural cloth/rigging owns these). */
  bakedMaterialNames: [/sail/i, /rigging/i, /cloth/i],
} as const;

export interface GltfMaterialOptions {
  /**
   * Ambient reflection strength. The storm sky environment is fairly dim, so
   * realistic assets default to a restrained 0.55 instead of three's default 1.
   */
  envMapIntensity?: number;
  /** Clamp texture anisotropy to this value (default `PBR_MATERIAL_RULES.maxAnisotropy`). */
  maxAnisotropy?: number;
}

export interface ApplyPbrDisciplineOptions extends GltfMaterialOptions {
  /** Hide meshes whose materials match these names (default: baked sails/rigging). */
  hideMaterials?: RegExp[];
}

function isMeshStandardMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return (material as THREE.MeshStandardMaterial).isMeshStandardMaterial === true;
}

function isMeshBasicMaterial(material: THREE.Material): material is THREE.MeshBasicMaterial {
  return (material as THREE.MeshBasicMaterial).isMeshBasicMaterial === true;
}

/** Re-assert the colour-space contract on every PBR map of a material. */
function normalizeColorSpaces(material: THREE.MeshStandardMaterial): void {
  if (material.map) material.map.colorSpace = THREE.SRGBColorSpace;
  if (material.emissiveMap) material.emissiveMap.colorSpace = THREE.SRGBColorSpace;

  if (material.normalMap) material.normalMap.colorSpace = THREE.NoColorSpace;
  if (material.roughnessMap) material.roughnessMap.colorSpace = THREE.NoColorSpace;
  if (material.metalnessMap) material.metalnessMap.colorSpace = THREE.NoColorSpace;
  if (material.aoMap) material.aoMap.colorSpace = THREE.NoColorSpace;
  if (material.bumpMap) material.bumpMap.colorSpace = THREE.NoColorSpace;
  if (material.alphaMap) material.alphaMap.colorSpace = THREE.NoColorSpace;
}

/**
 * Apply the PBR discipline to one material: colour spaces, anisotropy clamp and
 * (optionally) an `envMapIntensity` override. Safe to call on any material.
 */
export function configureGltfMaterial(
  material: THREE.Material,
  options: GltfMaterialOptions = {},
): THREE.Material {
  const maxAnisotropy = options.maxAnisotropy ?? PBR_MATERIAL_RULES.maxAnisotropy;
  let maps: Array<THREE.Texture | null> = [];

  if (isMeshStandardMaterial(material)) {
    normalizeColorSpaces(material);
    if (options.envMapIntensity !== undefined) {
      material.envMapIntensity = options.envMapIntensity;
    }
    maps = [
      material.map,
      material.normalMap,
      material.roughnessMap,
      material.metalnessMap,
      material.aoMap,
      material.emissiveMap,
    ];
  } else if (isMeshBasicMaterial(material)) {
    // KHR_materials_unlit -> MeshBasicMaterial: only the base colour is encoded.
    if (material.map) material.map.colorSpace = THREE.SRGBColorSpace;
    maps = [material.map];
  }

  for (const map of maps) {
    if (map) map.anisotropy = Math.max(1, Math.min(map.anisotropy || 1, maxAnisotropy));
  }

  return material;
}

/**
 * Wire one packed ORM texture into a standard material (AO->R, Roughness->G,
 * Metalness->B). Use this when the offline pipeline bakes the three Poly Haven /
 * AmbientCG maps into a single RGB texture. Setting the scalar factors to 1
 * lets the map fully drive each channel.
 */
export function applyOrmPacking(
  material: THREE.MeshStandardMaterial,
  ormTexture: THREE.Texture,
): THREE.MeshStandardMaterial {
  ormTexture.colorSpace = THREE.NoColorSpace;

  material.aoMap = ormTexture;
  material.aoMapIntensity = 1;
  material.roughnessMap = ormTexture;
  material.metalnessMap = ormTexture;

  material.roughness = 1;
  material.metalness = 1;
  material.needsUpdate = true;
  return material;
}

/**
 * Run the full discipline pass over a freshly loaded model's scene root (the
 * `scene` of a `GLTF`, or any `THREE.Group`): colour spaces, anisotropy,
 * `envMapIntensity`, and hide any mesh made purely of baked sail/rigging
 * material (the procedural cloth sails own that look).
 */
export function applyPbrDiscipline(
  root: THREE.Object3D,
  options: ApplyPbrDisciplineOptions = {},
): THREE.Object3D {
  const hideMaterials = options.hideMaterials ?? [...PBR_MATERIAL_RULES.bakedMaterialNames];

  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!(mesh as THREE.Mesh).isMesh) return;

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) configureGltfMaterial(material, options);

    const purelyBaked =
      materials.length > 0 &&
      materials.every((material) => hideMaterials.some((pattern) => pattern.test(material.name)));
    if (purelyBaked) mesh.visible = false;
  });

  return root;
}
